import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageCTA from "@/components/PageCTA";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ExternalLink, Lock, Play } from "lucide-react";

// Extract YouTube ID from any common URL shape
function youtubeId(url = "") {
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
}
function vimeoId(url = "") {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : null;
}

function VideoEmbed({ url, title }) {
    const yt = youtubeId(url);
    const vm = vimeoId(url);
    if (yt) {
        return (
            <iframe
                src={`https://www.youtube.com/embed/${yt}?rel=0`}
                title={title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        );
    }
    if (vm) {
        return (
            <iframe
                src={`https://player.vimeo.com/video/${vm}`}
                title={title}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
            />
        );
    }
    return (
        <a href={url} target="_blank" rel="noreferrer" className="flex items-center justify-center w-full h-full text-[var(--sb-brand)] hover:text-[var(--sb-text)] gap-2">
            <Play size={20} /> Open video
        </a>
    );
}

export default function CommunitySpotlight() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/community/submissions")
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, []);

    const isSubscriber = user?.is_subscribed || user?.role === "admin";

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28" data-testid="community-spotlight-page">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
                <div className="lg:col-span-7">
                    <div className="overline mb-3">Community Spotlight</div>
                    <h1 className="font-serif text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
                        Your Scotland,<br/>
                        <span className="italic text-[var(--sb-brand)]">told back to us.</span>
                    </h1>
                </div>
                <div className="lg:col-span-5 lg:col-start-8 self-end space-y-4">
                    <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed">
                        Footage and stories from subscribers. Every clip here was sent in by someone who paid £1.89 a month, picked up a controller, and went somewhere worth flying.
                    </p>
                    {isSubscriber ? (
                        <Link to="/dashboard" className="btn-primary text-sm" style={{padding: "0.55rem 1.1rem"}} data-testid="spotlight-submit-cta">
                            Submit your footage <ArrowRight size={16} />
                        </Link>
                    ) : (
                        <Link to="/subscription" className="btn-outline text-sm" style={{padding: "0.55rem 1.1rem"}} data-testid="spotlight-subscribe-cta">
                            <Lock size={14} /> Subscribe to contribute (£1.89)
                        </Link>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="space-y-12">
                    {[...Array(2)].map((_, i) => (
                        <div key={i} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            <div className="lg:col-span-7 aspect-video bg-[var(--sb-surface)] animate-pulse" />
                            <div className="lg:col-span-5 space-y-3">
                                <div className="h-6 w-40 bg-[var(--sb-surface)] animate-pulse" />
                                <div className="h-10 w-3/4 bg-[var(--sb-surface)] animate-pulse" />
                                <div className="h-24 bg-[var(--sb-surface)] animate-pulse" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="relative overflow-hidden border border-[var(--sb-border)]" data-testid="spotlight-empty">
                    {/* Cinematic Glencoe backdrop — Three Sisters in the mist.
                        Sits behind the empty-state copy at 40% brightness so the
                        text stays readable but the box never feels empty. */}
                    <div className="relative aspect-[16/9] sm:aspect-[21/9]">
                        <img
                            src="https://images.pexels.com/photos/31300997/pexels-photo-31300997.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
                            alt="Misty view of the Three Sisters in Glencoe, Scotland"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-[var(--sb-bg)]/50 via-[var(--sb-bg)]/70 to-[var(--sb-bg)]/95" />
                        <div className="absolute inset-0 flex items-center justify-center p-8 lg:p-16 text-center">
                            <div className="max-w-md">
                                <div className="overline mb-4" style={{ color: "#a8d0ef", fontWeight: 700 }}>
                                    <span className="editorial-rule" style={{ background: "#0055A4" }} />
                                    Quiet on the wall
                                </div>
                                <h2 className="font-serif text-3xl lg:text-5xl tracking-tight leading-[1] mb-5 text-white">
                                    No submissions <span className="italic" style={{ color: "#a8d0ef" }}>yet.</span>
                                </h2>
                                <p className="text-[var(--sb-text-dim)] mb-8 leading-relaxed">
                                    Be the first to land here. If you&rsquo;ve flown somewhere in Scotland that means something to you, send the clip and the story.
                                </p>
                                {isSubscriber ? (
                                    <Link to="/dashboard" className="btn-primary"><ArrowRight size={16}/> Submit yours</Link>
                                ) : (
                                    <Link to="/subscription" className="btn-primary"><Lock size={14}/> Subscribe — £1.89</Link>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-20" data-testid="spotlight-list">
                    {items.map((s, i) => {
                        const reverse = i % 2 === 1;
                        return (
                            <article key={s.id} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-testid={`spotlight-item-${s.id}`}>
                                <div className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
                                    <div className="aspect-video bg-black border border-[var(--sb-border)]">
                                        <VideoEmbed url={s.video_url} title={s.title} />
                                    </div>
                                </div>
                                <div className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
                                    <div className="overline mb-3">{s.location}</div>
                                    <h2 className="font-serif text-3xl lg:text-5xl tracking-tighter leading-[1] mb-4">{s.title}</h2>
                                    <div className="text-sm uppercase tracking-[0.2em] text-[var(--sb-brand)] mb-5">
                                        by {s.display_name || s.user_name || "Anonymous"}
                                    </div>
                                    {s.note ? (
                                        <div className="space-y-4 text-[var(--sb-text-dim)] leading-relaxed">
                                            {s.note.split(/\n\n+/).map((para, idx) => (
                                                <p key={idx}>{para}</p>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-[var(--sb-text-muted)] italic">No story given.</p>
                                    )}
                                    <div className="mt-6 text-xs text-[var(--sb-text-muted)] flex items-center gap-3 uppercase tracking-[0.2em]">
                                        <span>{new Date(s.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                                        <a href={s.video_url} target="_blank" rel="noreferrer" className="hover:text-[var(--sb-brand)] inline-flex items-center gap-1">
                                            Open original <ExternalLink size={11} />
                                        </a>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
            <PageCTA variant="journey" className="!px-0" />
        </div>
    );
}
